import ApiClientsService from 'js/services/api/api-clients.js'
import _, {get as lodashGet} from 'lodash'

const mergeUniqueItems = (items = [], getKey) => {
  const output = []
  const seen = new Set()

  items.forEach(item => {
    if (!item) return

    const key = getKey(item)
    if (!key || seen.has(key)) {
      if (!key) output.push(item)

      return
    }

    seen.add(key)
    output.push(item)
  })

  return output
}

const normalizeClientMediaResponse = (items = []) => {
  if (!Array.isArray(items) || items.length === 0) return {}

  return {
    ...(items[0] || {}),
    transactions: mergeUniqueItems(
      items.flatMap(item => item?.transactions || []),
      item => _.get(item, '_id') || item?.id || item?.operation_id
    ),
    product_items: mergeUniqueItems(
      items.flatMap(item => item?.product_items || []),
      item => _.get(item, '_id') || item?.id || item?.external_code || item?.media_code
    ),
    tnes: mergeUniqueItems(
      items.flatMap(item => item?.tnes || []),
      item => _.get(item, '_id') || item?.id || item?.account_id || item?.external_code ||
        item?.media_code
    ),
    media: mergeUniqueItems(
      items,
      item => (
        _.get(item, '_id') || item?.id || item?.CardSerialNumber || item?.external_code ||
        item?.media_code
      )
    )
  }
}

export default {
  account: {
    state: {
      fullClient: {},
      mediaTypes: []
    },
    getters: {
      fullClient: state => state.fullClient,
      paymentMethods: (state, getters, rootState, rootGetters) => {
        const accts = state?.fullClient?.tnes || []
        const area_id = rootGetters['administrativeArea/administrativeAreaId']

        return accts
          .filter(tne => tne.administrative_area_id === area_id)
          .map(i => ({
            id: i.id || lodashGet(i, '_id'),
            account_id: i.account_id,
            alias: i.alias,
            external_code: i.external_code,
            media_code: i.media_code
          }))
      },
      mediaTypes: state => state.mediaTypes
    },
    mutations: {
      setFullClient(state, data) {
        state.fullClient = data
      },
      setMediaTypes(state, data) {
        state.mediaTypes = data
      }
    },
    actions: {
      /**
     * @param {Object} context - The Vuex action context ({ commit, state, getters, ... })
     * @param {Object} payload - Optional object containing the current account details.
     * @param {String} payload.administrative_area_id optional
     * @param {string} payload.rut optional
     * @returns {Promise<Object>} A promise that resolves with the client media data,
     * or rejects if the API call fails.
     */
      async getAllClientData({commit, getters, dispatch, rootGetters}, payload = {}) {
        if (!_.isEmpty(getters.fullClient)) return getters.fullClient

        // Construct the API parameters
        let administrative_area_id = null
        let rut = null

        if (!_.isEmpty(payload?.administrative_area_id)) {
          administrative_area_id = payload.administrative_area_id
        } else {
          administrative_area_id = rootGetters['administrativeArea/administrativeAreaId'] || null
        }

        if (!_.isEmpty(payload?.rut)) {
          rut = payload.rut
        } else {
          rut = rootGetters.currentClientAccount?.rut || null
        }

        if (!administrative_area_id || !rut) return null

        const params = {
          administrative_area_id,
          rut
        }

        try {
          const response = await ApiClientsService.clientMediaInfo(params)
          const data = normalizeClientMediaResponse(response.data)

          commit('setFullClient', data)

          dispatch('getPaymentMethods', params)

          return data
        } catch (error) {
          console.error('API Error in checkIfCanRequestTravels:', error)
          throw error
        }
      },

      async getPaymentMethods({commit}, payload = {}) {
        const response = await ApiClientsService.mediaTypes(payload)

        try {
          commit('setMediaTypes', response)

          return response
        } catch (error) {
          console.error('API Error: trying to get media types:', error)
          throw error
        }
      }
    }
  }
}
